// InKindOrdersReport.js
import * as React from "react";
import { useState, useEffect } from "react";
import {
  Title,
  Datagrid,
  TextField,
  DateField,
  useDataProvider,
  useNotify,
  ReferenceField,
  FunctionField,
  usePermissions,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  MenuItem,
  Select,
  Chip,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  BarChart,
  PieChart,
  ChartsTooltip,
  BarElement,
} from "@mui/x-charts";
import {
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  CheckCircle as ReceivedIcon,
  HourglassEmpty as InTransitIcon,
  Cancel as CancelledIcon,
  Category as TypeIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OrderStatusUpdateModal from "../components/StatusUpdateDialog";

// In-kind order status icon
const InKindStatusIcon = ({ status }) => {
  const iconProps = {
    fontSize: "small",
    sx: { mr: 1 },
  };

  switch (status) {
    case "Dispatched":
      return <InventoryIcon color="info" {...iconProps} />;
    case "InTransit":
      return <ShippingIcon color="warning" {...iconProps} />;
    case "Delivered":
      return <ShippingIcon color="success" {...iconProps} />;
    case "Received":
      return <ReceivedIcon color="success" {...iconProps} />;
    case "Cancelled":
      return <CancelledIcon color="error" {...iconProps} />;
    default:
      return <InventoryIcon color="action" {...iconProps} />;
  }
};

// Status field for in-kind orders
const InKindStatusField = ({ record }) => {
  const theme = useTheme();

  const statusColors = {
    Dispatched: theme.palette.info.main,
    InTransit: theme.palette.warning.main,
    Delivered: theme.palette.success.main,
    Received: theme.palette.success.dark,
    Cancelled: theme.palette.error.main,
  };

  const statusLabels = {
    Dispatched: "Dispatched",
    InTransit: "In Transit",
    Delivered: "Delivered",
    Received: "Received",
    Cancelled: "Cancelled",
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <InKindStatusIcon status={record?.inKindStatus} />
      <Chip
        label={statusLabels[record?.inKindStatus] || record?.inKindStatus}
        size="small"
        sx={{
          backgroundColor: alpha(statusColors[record?.inKindStatus] || theme.palette.grey[300], 0.1),
          color: statusColors[record?.inKindStatus] || theme.palette.text.primary,
          fontWeight: 500,
        }}
      />
    </Box>
  );
};

// Summary card component
const SummaryCard = ({ title, value, icon: Icon, color }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        borderLeft: `4px solid ${color || theme.palette.primary.main}`,
        background: `linear-gradient(135deg, ${alpha(
          color || theme.palette.primary.light,
          0.1
        )} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Icon sx={{ mr: 1, color: color || theme.palette.primary.main }} />
          <Typography variant="subtitle2" color="textSecondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

// In-kind orders list component
export const InKindOrderList = (props) => {
  return (
    <Datagrid rowClick="show">
      <TextField source="id" label="ID" />
      <ReferenceField source="buyer.id" reference="users" label="Buyer">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField source="negotiation.seller.id" reference="users" label="Seller">
        <TextField source="name" />
      </ReferenceField>
      <FunctionField
        label="Type"
        render={(record) =>
          record.negotiation?.type === "Crop" ? "Crop" :
            record.negotiation?.type === "Service" ? "Service" :
              record.negotiation?.type || "-"
        }
      />
      <FunctionField
        label="Quantity"
        render={(record) => `${record.quantity || 0}`}
      />
      <FunctionField
        label="Value"
        render={(record) =>
          record.price > 0 ? `${record.price} SDG` : "Free"
        }
      />
      <FunctionField
        label="Status"
        render={(record) => <InKindStatusField record={record} />}
      />
      <DateField source="createdAt" label="Created Date" showTime />
      <DateField source="deliveryDate" label="Delivery Date" />
    </Datagrid>
  );
};

// Main in-kind orders report component
const InKindOrdersReport = () => {
  const { permissions } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const theme = useTheme();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // MODAL STATE - Moved inside the component
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrderForUpdate, setSelectedOrderForUpdate] = useState(null);

  // Get token (if using authentication)
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  // Check permissions
  const isManager = permissions === 'manager';
  const canSeeActionButtons = !isManager && (permissions === 'admin' || permissions === 'operater');

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Status options for in-kind orders
  const statusOptions = [
    "Dispatched",
    "InTransit",
    "Delivered",
    "Received",
    "Cancelled"
  ];

  // Type options
  const typeOptions = ["Crop", "Service"];

  // Create a simple action button component - Moved inside the component
  const OrderActionButtons = ({ order }) => {
     if (!canSeeActionButtons) {
      return null;
    }
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => window.open(`/#/orders/${order.id}/show`, '_blank')}
            color="primary"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Update Status">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedOrderForUpdate(order);
              setShowStatusModal(true);
            }}
            color="secondary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  useEffect(() => {
    const fetchInKindOrdersData = async () => {
      try {
        setLoading(true);

        // Build query parameters
        const params = new URLSearchParams();
        params.append("isInKind", "true"); // Always filter for in-kind orders

        if (statusFilter) params.append("inKindStatus", statusFilter);
        if (typeFilter) params.append("negotiationType", typeFilter);
        if (startDate) params.append("fromDate", startDate);
        if (endDate) params.append("toDate", endDate);

        // Fetch orders data
        const { data: orders } = await dataProvider.getList("orders", {
          filter: { isInKind: true },
          pagination: { page: 1, perPage: 50 },
          sort: { field: "createdAt", order: "DESC" },
        });

        // Calculate statistics
        const stats = {
          total: orders.length,
          dispatched: orders.filter(o => o.inKindStatus === "Dispatched").length,
          inTransit: orders.filter(o => o.inKindStatus === "InTransit").length,
          delivered: orders.filter(o => o.inKindStatus === "Delivered").length,
          received: orders.filter(o => o.inKindStatus === "Received").length,
          cancelled: orders.filter(o => o.inKindStatus === "Cancelled").length,
          crops: orders.filter(o => o.negotiation?.type === "Crop").length,
          services: orders.filter(o => o.negotiation?.type === "Service").length,
        };

        // Status distribution for chart
        const statusDistribution = Object.entries({
          Dispatched: stats.dispatched,
          InTransit: stats.inTransit,
          Delivered: stats.delivered,
          Received: stats.received,
          Cancelled: stats.cancelled,
        }).map(([status, count]) => ({ status, count }));

        // Type distribution for chart
        const typeDistribution = [
          { type: "Crop", count: stats.crops },
          { type: "Service", count: stats.services },
        ];

        // Monthly trend (simplified - you might want to implement proper grouping)
        const currentDate = new Date();
        const currentMonth = currentDate.toLocaleString('default', { month: 'short' });
        const timeSeries = [
          { month: currentMonth, count: stats.total }
        ];

        setReportData({
          summary: stats,
          statusDistribution,
          typeDistribution,
          timeSeries,
          orders,
        });

      } catch (err) {
        console.error("Error fetching in-kind orders data:", err);
        setError(err.message);
        notify("Error loading in-kind orders data", { type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchInKindOrdersData();
  }, [dataProvider, notify, statusFilter, typeFilter, startDate, endDate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load in-kind orders data: {error}
      </Alert>
    );
  }

  const { summary, statusDistribution, typeDistribution, timeSeries, orders } = reportData || {};

  return (
    <Box sx={{ p: 3 }}>
      <Title title="In-Kind Orders Report" />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" color="primary">
          In-Kind Contributions Report
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage and track all in-kind contribution orders
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Total Contributions"
            value={summary?.total || 0}
            icon={TimelineIcon}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Crops"
            value={summary?.crops || 0}
            icon={TypeIcon}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Services"
            value={summary?.services || 0}
            icon={TypeIcon}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            title="Completed"
            value={summary?.received || 0}
            icon={ReceivedIcon}
            color={theme.palette.success.main}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2">Contribution Status</Typography>
            <Select
              fullWidth
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {statusOptions.map(status => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2">Contribution Type</Typography>
            <Select
              fullWidth
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {typeOptions.map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={3}>
            <DatePicker
              label="From Date"
              value={startDate || null}
              onChange={(newDate) => {
                setStartDate(newDate ? newDate.toISOString().slice(0, 10) : "");
              }}
              renderInput={(params) => (
                <MuiTextField {...params} fullWidth size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <DatePicker
              label="To Date"
              value={endDate || null}
              onChange={(newDate) => {
                setEndDate(newDate ? newDate.toISOString().slice(0, 10) : "");
              }}
              renderInput={(params) => (
                <MuiTextField {...params} fullWidth size="small" />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Overview" />
            <Tab label="Contributions List" />
          </Tabs>
        </Paper>
      </Box>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Status Distribution Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <PieChart
                    series={[{
                      data: statusDistribution?.map(item => ({
                        id: item.status,
                        label: item.status,
                        value: item.count,
                      })) || [],
                      innerRadius: 40,
                      outerRadius: 100,
                    }]}
                  >
                    <ChartsTooltip />
                  </PieChart>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Type Distribution Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Type Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <BarChart
                    dataset={typeDistribution || []}
                    xAxis={[{
                      scaleType: 'band',
                      dataKey: 'type',
                    }]}
                    series={[{
                      dataKey: 'count',
                      label: 'Number of Contributions',
                    }]}
                  >
                    <ChartsTooltip />
                    <BarElement />
                  </BarChart>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Orders List Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              In-Kind Contributions List ({orders?.length || 0})
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: theme.palette.grey[100] }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Order ID</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Buyer</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Seller</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Quantity</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Value</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Created Date</th>
                    {canSeeActionButtons && (
                      <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orders?.map((order) => (
                    <tr key={order.id} style={{ borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
                      <td style={{ padding: '12px' }}>#{order.id}</td>
                      <td style={{ padding: '12px' }}>{order.buyer?.name || '-'}</td>
                      <td style={{ padding: '12px' }}>{order.negotiation?.seller?.name || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        {order.negotiation?.type === "Crop" ? "Crop" :
                          order.negotiation?.type === "Service" ? "Service" : "-"}
                      </td>
                      <td style={{ padding: '12px' }}>{order.quantity || 0}</td>
                      <td style={{ padding: '12px' }}>
                        {order.price > 0 ? `${order.price} SDG` : "Free"}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <InKindStatusField record={order} />
                      </td>
                      <td style={{ padding: '12px' }}>
                        {new Date(order.createdAt).toLocaleDateString('en-US')}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <OrderActionButtons order={order} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(!orders || orders.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="textSecondary">
                    No in-kind contributions to display
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Comment out for now until you create the modal file */}
      {showStatusModal && (
        <OrderStatusUpdateModal
          open={showStatusModal}
          onClose={(refresh) => {
            setShowStatusModal(false);
            setSelectedOrderForUpdate(null);
            if (refresh) {
              // Refresh your data
              // fetchInKindOrdersData();
            }
          }}
          selectedOrder={selectedOrderForUpdate}
          token={token}
        />
      )}
    </Box>
  );
};

export default InKindOrdersReport;